import { Link } from 'react-router-dom';

const steps = [
  {
    number: 1,
    title: 'Create Your Account',
    description: 'Sign up with email and choose a plan. Your account is ready in seconds.',
    icon: 'person_add',
    details: [
      'Enter your email and password',
      'Choose Free or Pro ($15/mo)',
      'Verify your email',
      'Access your dashboard immediately',
    ],
  },
  {
    number: 2,
    title: 'Build Your Docker Image',
    description: 'Package your app in a Docker container. Works with any language or framework.',
    icon: 'inventory_2',
    details: [
      'Write a Dockerfile for your app',
      'Build locally with `docker build`',
      'Test locally with `docker run`',
      'Tag your image for deployment',
    ],
    code: `# Build your app image
docker build -t myapp:latest .

# Test locally
docker run -p 8080:8080 myapp:latest`,
  },
  {
    number: 3,
    title: 'Get Your API Key',
    description: 'Generate an API key from your dashboard to authenticate deployments.',
    icon: 'vpn_key',
    details: [
      'Go to Settings > API Keys',
      'Create a new key with deploy scope',
      'Copy the key securely',
      'Use in CI/CD or AI assistant',
    ],
    code: `# Or create via API
curl -X POST https://api.wiserwebcloud.com/api/api-keys \\
  -H "Authorization: Bearer $TOKEN" \\
  -d '{"name":"deploy-key","scopes":["deploy"]}'`,
  },
  {
    number: 4,
    title: 'Deploy via API',
    description: 'Push your image and create a deployment with a single API call.',
    icon: 'cloud_upload',
    details: [
      'Push image to WiserWeb registry',
      'Create deployment via API',
      'Set environment variables',
      'Configure custom domain (Pro)',
    ],
    code: `# Deploy your app
curl -X POST https://api.wiserwebcloud.com/api/deployments \\
  -H "X-API-Key: ww_live_..." \\
  -d '{"name":"myapp","image":"myapp:latest"}'`,
  },
  {
    number: 5,
    title: 'Your App is Live!',
    description: 'That\'s it. Your app is accessible at your *.io.wiserwebcloud.com subdomain.',
    icon: 'check_circle',
    details: [
      'App served via our container platform',
      'Free SSL certificate included',
      'Real-time analytics in dashboard',
      'Scale up anytime on Pro',
    ],
  },
];

export default function HowItWorksPage() {
  return (
    <div className="pt-16">
      {/* Hero */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-orange-500/5 via-transparent to-transparent" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6">
            From Zero to{' '}
            <span className="gradient-text">Production</span>{' '}
            in Minutes
          </h1>
          <p className="text-xl text-zinc-400 max-w-2xl mx-auto">
            See exactly how WISERWEB works. It's simpler than you think.
          </p>
        </div>
      </section>

      {/* Steps */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            {steps.map((step, index) => (
              <div key={step.number} className="relative">
                {/* Connection line */}
                {index < steps.length - 1 && (
                  <div className="absolute left-8 top-20 w-0.5 h-full bg-gradient-to-b from-orange-500 to-transparent" />
                )}

                <div className="flex gap-8">
                  {/* Step number */}
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center shadow-glow">
                      <span className="material-icons text-white text-2xl">{step.icon}</span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-sm font-bold text-orange-400">STEP {step.number}</span>
                    </div>
                    <h2 className="text-2xl font-bold text-white mb-3">{step.title}</h2>
                    <p className="text-lg text-zinc-400 mb-6">{step.description}</p>

                    <div className="grid md:grid-cols-2 gap-6">
                      {/* Details list */}
                      <ul className="space-y-3">
                        {step.details.map((detail, i) => (
                          <li key={i} className="flex items-start gap-3 text-zinc-300">
                            <span className="material-icons text-green-400 text-lg mt-0.5">check_circle</span>
                            {detail}
                          </li>
                        ))}
                      </ul>

                      {/* Code example if present */}
                      {step.code && (
                        <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
                          <div className="flex items-center gap-2 px-4 py-2 bg-zinc-800/50 border-b border-zinc-800">
                            <div className="w-3 h-3 rounded-full bg-red-500" />
                            <div className="w-3 h-3 rounded-full bg-yellow-500" />
                            <div className="w-3 h-3 rounded-full bg-green-500" />
                            <span className="ml-2 text-xs text-zinc-500 font-mono">terminal</span>
                          </div>
                          <pre className="p-4 text-sm overflow-x-auto">
                            <code className="text-zinc-300">{step.code}</code>
                          </pre>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI Assistant Examples */}
      <section className="py-24 bg-raised">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-white mb-4">
              Works with Your Favorite{' '}
              <span className="gradient-text">AI Assistants</span>
            </h2>
            <p className="text-lg text-zinc-400 max-w-2xl mx-auto">
              Just tell your AI to deploy, and it handles the rest.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Cursor */}
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center mb-4">
                <span className="text-white font-bold text-lg">C</span>
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Cursor</h3>
              <p className="text-zinc-400 mb-4">
                "Deploy my project to WiserWebCloud using my API key."
              </p>
              <div className="bg-zinc-800/50 rounded-lg p-3 text-sm text-zinc-300 font-mono">
                Cursor calls the WiserWeb API to deploy your container
              </div>
            </div>

            {/* Claude */}
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center mb-4">
                <span className="text-white font-bold text-lg">A</span>
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Claude</h3>
              <p className="text-zinc-400 mb-4">
                "Build and deploy this app to my WiserWebCloud account."
              </p>
              <div className="bg-zinc-800/50 rounded-lg p-3 text-sm text-zinc-300 font-mono">
                Claude uses the WiserWeb API to create a deployment
              </div>
            </div>

            {/* GitHub Copilot */}
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-gray-700 to-gray-900 flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">GitHub Copilot</h3>
              <p className="text-zinc-400 mb-4">
                "Push this to WiserWebCloud and set up the domain."
              </p>
              <div className="bg-zinc-800/50 rounded-lg p-3 text-sm text-zinc-300 font-mono">
                Copilot generates the API calls for deployment
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">
            Ready to Try It Yourself?
          </h2>
          <p className="text-lg text-zinc-400 mb-8">
            Start deploying with AI in under 5 minutes.
          </p>
          <Link
            to="/register"
            className="inline-flex items-center justify-center gap-2 px-8 py-4 text-lg font-semibold text-white bg-gradient-to-r from-orange-500 to-red-500 rounded-xl hover:shadow-glow-lg transition-all"
          >
            <span className="material-icons">rocket_launch</span>
            Get Started Free
          </Link>
        </div>
      </section>
    </div>
  );
}

